import { LitElement, html } from 'lit';
import {
  updateMicroStepEvent,
  displayProgressBarEvent,
  updateMacroStepEvent,
} from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { HighlineService } from '@cof/auto-refinance-node-highline';
import {
  EXTERNAL_ID_SESSION_STORAGE_KEY,
  NewRelicCustomAttribute,
  isWebView,
  parseChannelFromExternalId,
} from '@cof/refi-web-sdk/dist/utils/stratum';
import { SessionService } from './core/services/session/session.service.js';
import { DebugService } from './shared/services/debug.service.js';
import { style } from './app.style.js';
import { VehicleService } from './services/vehicle.service.js';
import { ApiService } from './core/services/api/api.service.js';
import { ApplicationService } from './services/application.service.js';
import './components/iconic-vehicle-and-loan-container.component.js';
import { REFI_VEHICLE_LOAN_CONST } from './shared/constants/app.constants.js';
import { VehicleAndLoanService } from './services/vehicle-and-loan.service.js';
import { stratum } from '../constants/stratum.js';
import { NEW_RELIC_PLUGIN_NAME } from '../utils/new-relic-plugin.js';
import { environment } from '../environment/environment.js';
import './components/review/vehicle-review.component.js';
import { AppService } from './app.service.js';
import { CookieService } from './core/services/cookie/cookie.service.js';
import { PolicyLimitsService } from './shared/services/policy-limits.service.js';
import { PerformanceTrackingService } from './services/performanceTracking.service.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';

class RefiVehicleAndLoanApp extends LitElement {
  constructor() {
    super();
    this.lighthouseSetup();
    this.debug = new DebugService('RefiVehicleAndLoanApp');
    this.session = SessionService.session || '';
    this.loadingApp = false;
    this.onProcessingPage = false;
    this.showMfe = false;
    this.baseHref = document.querySelector('base').getAttribute('href');
    this.showAnimation = true;
    this.hasSeenAnimation = false;
    this.hasSeenStopAnimation = false;
    this.existingExperimentIds = [];
    this.firstLoad = true;
    PerformanceTrackingService.performanceTrackStart('vehicle');
  }

  static properties = {
    referenceId: { type: String, attribute: 'client-correlation-id' },
    application: {
      type: Object,
      reflect: true,
      hasChanged: (newVal, oldVal) => {
        ApplicationService.hasConsent = !!newVal.hasConsent;
        const applicantUnchanged =
          REFI_VEHICLE_LOAN_CONST.CHANGED_APPLICANT_KEYS.every((key) => {
            if (key.indexOf('.') > 0) {
              const [outerPath, innerPath] = key.split('.');
              return (
                newVal?.primaryApplicant[outerPath][innerPath] ===
                oldVal?.primaryApplicant[outerPath][innerPath]
              );
            }
            return (
              newVal?.primaryApplicant[key] === oldVal?.primaryApplicant[key]
            );
          });
        const addressUnchanged =
          REFI_VEHICLE_LOAN_CONST.CHANGED_ADDRESS_KEYS.every((key) => {
            if (key.indexOf('.') > 0) {
              const [outerPath, innerPath] = key.split('.');
              return (
                newVal?.primaryApplicant[outerPath][innerPath] ===
                oldVal?.primaryApplicant[outerPath][innerPath]
              );
            }
            return (
              newVal?.primaryApplicant[key] === oldVal?.primaryApplicant[key]
            );
          });
        if (!applicantUnchanged) {
          if (
            VehicleAndLoanService.validateApplication(newVal) &&
            ApplicationService.hasConsent
          ) {
            if (!addressUnchanged) {
              VehicleAndLoanService.searchVehicleAndLoan(newVal);
            } else {
              VehicleAndLoanService.doVehicleAndLoanSearch(newVal);
            }
          } else {
            VehicleAndLoanService.resetVehicleAndLoanService();
          }
          return true;
        }
        return false;
      },
    },
    customerOffer: { type: Object, attribute: 'customer-offer' },
    upfrontOffers: { type: Boolean, attribute: 'upfront-offers' },
    experiments: {
      type: Object,
      hasChanged: (newVal, oldVal) => {
        if (newVal !== oldVal) {
          const experimentIds = [];
          Object.keys(newVal).forEach((experiment) => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            } else {
              experimentIds.push(`${experiment}-ineligible`);
            }
          });
          ApplicationService.updateExperiments(experimentIds);
          ApplicationService.updateExperiments(
            experimentIds,
            this.existingExperimentIds
          );
          this.existingExperimentIds = experimentIds;
          return true;
        }
        return false;
      },
    },
    preload: { type: String, attribute: 'pre-load' },
    checkout: { type: Boolean, attribute: 'checkout-page' },
    showAnimation: { type: Boolean },
    loadingApp: { type: Boolean },
  };

  async connectedCallback() {
    this.loadingApp = true;
    super.connectedCallback();

    this.setupFromParameters();

    ApiService.clientCorrelationId = this.referenceId;
    ApplicationService.setApplication(this.application);
    ApplicationService.MarketingOffer = this.customerOffer;
    ApplicationService.isUpfrontOffers = this.upfrontOffers;
    VehicleService.setResidentState(
      ApplicationService.applicant?.currentAddress?.stateCode
    );
    HighlineService.urlPrefix = REFI_VEHICLE_LOAN_CONST.HIGHLINE.URL_PREFIX();
    HighlineService.getPublicKey(REFI_VEHICLE_LOAN_CONST.HIGHLINE.PRODUCT_ID());
    PolicyLimitsService.initialize();
    ApplicationService.updateInAppDeclineDelayToggle();
    ApplicationService.updateIconicV2();
    await FeatureToggleService.updateIsInvisiblePQ();

    ApplicationService.isIconicAndToggledOn = true;

    this.setConsentFromApplication();

    this.hasSeenAnimation =
      ApplicationService.Application?.hasSeenValAnimation ?? false;

    if (ApplicationService.hasConsent) {
      if (VehicleAndLoanService.validateApplication()) {
        VehicleAndLoanService.doVehicleAndLoanSearch()
          .then(() => {
            this.loadingApp = false;
          })
          .catch(() => {
            this.loadingApp = false;
          });
        if (!this.hasSeenAnimation) {
          this.hasSeenAnimation = true;
          ApplicationService.Application.hasSeenValAnimation = true;
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName:
                  REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                    .initiateAnimationTransition,
                data: {
                  minimumAnimationLengthMs: 5300,
                  animationText: 'Importing your car and loan info...',
                  animationCompleteText: 'Got it!',
                },
              },
            })
          );
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName:
                  REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                    .initiateAnimationCompletion,
                data: {
                  showCompletionAnimation: true,
                  successAnimationLength: 1500,
                  skipCurtainCloseAnimation: false,
                },
              },
            })
          );
        }
      }
    } else {
      this.showAnimation = false;
      this.loadingApp = false;
    }

    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.PRODUCT_TYPE,
      ApplicationService.MarketingOffer?.offerTypeCode ?? 'BAU'
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.IS_WEBVIEW,
      isWebView(navigator.userAgent)
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.EXTERNAL_ID,
      SessionService.getKey(EXTERNAL_ID_SESSION_STORAGE_KEY)
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.CHANNEL,
      parseChannelFromExternalId(
        SessionService.getKey(EXTERNAL_ID_SESSION_STORAGE_KEY)
      )
    );
    const parsedCookie = CookieService.getCookie(
      REFI_VEHICLE_LOAN_CONST.MARKETING_ATTRIBUTE_COOKIE
    )?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1];
    if (externalIdFromCookie && parsedCookie?.[0] === 'external_id') {
      stratum.setExternalVar('marketingChannel', externalIdFromCookie);
    }
    displayProgressBarEvent(true);
    if (this.upfrontOffers) {
      updateMicroStepEvent('');
      updateMacroStepEvent(
        REFI_VEHICLE_LOAN_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.macroStepName,
        REFI_VEHICLE_LOAN_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.microStepsList
      );
    } else {
      updateMacroStepEvent(
        REFI_VEHICLE_LOAN_CONST.PROGRESS_BAR_MICRO_STEPS.macroStepName,
        REFI_VEHICLE_LOAN_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList
      );
      const beginMicroStepIndex =
        REFI_VEHICLE_LOAN_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList.findIndex(
          (step) => step === REFI_VEHICLE_LOAN_CONST.STEPS()[0]
        );
      updateMicroStepEvent(
        REFI_VEHICLE_LOAN_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList[
          beginMicroStepIndex - 1
        ]
      );
    }
  }

  setupFromParameters() {
    this.experimentParam = new URL(window.location.href).searchParams.get(
      'experiment'
    );
    this.experimentNameParam = new URL(window.location.href).searchParams.get(
      'experimentName'
    );
    this.hasConsentParam = new URL(window.location.href).searchParams.get(
      'hasConsent'
    );
    if (!REFI_VEHICLE_LOAN_CONST.isProdEnv() && this.hasConsentParam) {
      ApplicationService.hasConsent = true;
    }
    if (
      !this.experimentNameParam &&
      this.experimentParam &&
      !REFI_VEHICLE_LOAN_CONST.isProdEnv()
    ) {
      this.experiments = {
        Iconic: {
          allocation: this.experimentParam,
          isEligible: true,
        },
      };
      this.debug.log(
        'Setting Iconic experiment by searchParams:',
        JSON.stringify(this.experiments)
      );
    } else if (
      this.experimentNameParam &&
      this.experimentParam &&
      !REFI_VEHICLE_LOAN_CONST.isProdEnv()
    ) {
      this.experiments = {
        [this.experimentNameParam]: {
          allocation: this.experimentParam,
          isEligible: true,
        },
      };
      this.debug.log(
        'Setting experiments by searchParams:',
        JSON.stringify(this.experiments)
      );
    }
  }

  setConsentFromApplication() {
    this.debug.log(
      'Consent',
      ApplicationService.Application?.hasConsent ?? false
    );
    ApplicationService.hasConsent =
      ApplicationService.Application?.hasConsent ?? false;
  }

  static styles = [style];

  lighthouseSetup() {
    if (!REFI_VEHICLE_LOAN_CONST.isProdEnv()) {
      AppService.testingParam = new URL(
        window.location.href
      )?.searchParams?.get('testing');
      if (AppService.testingParam === 'ICONIC_EMPTY') {
        ApplicationService.isIconicAndToggledOn = true;
      }
    }
  }

  render() {
    if (!this.loadingApp) {
      this.debug.log('rendered', this.baseHref, window.location.pathname);
      if (
        this.checkout ||
        (!environment.isProdEnv &&
          window.location.pathname === '/nonprod/review')
      ) {
        return html`
          <div role="main">
            <refi-vehicle-review></refi-vehicle-review>
          </div>
        `;
      }

      // if (ApplicationService.isInAppDeclineDelayOn) {
      //   return html`
      //     ${this.preload === 'true'
      //       ? ''
      //       : html` <div class="main" role="main">
      //           <refi-iconic-vehicle-and-loan-container></refi-iconic-vehicle-and-loan-container>
      //         </div>`}
      //   `;
      // }

      if (ApplicationService.isIconicAndToggledOn) {
        return html`
          ${this.preload === 'true'
            ? ''
            : html`
                <div class="main" role="main">
                  <refi-iconic-vehicle-and-loan-container
                    @start-animation="${() => {
                      if (!this.hasSeenAnimation) {
                        this.hasSeenAnimation = true;
                        ApplicationService.Application.hasSeenValAnimation = true;

                        this.dispatchEvent(
                          new CustomEvent('host-app-event', {
                            bubbles: true,
                            composed: true,
                            detail: {
                              actionName:
                                REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                                  .initiateAnimationTransition,
                              data: {
                                minimumAnimationLengthMs: 5300,
                                animationText:
                                  'Importing your car and loan info...',
                                animationCompleteText: 'Got it!',
                              },
                            },
                          })
                        );
                        // this.dispatchEvent(
                        //     new CustomEvent('host-app-event', {
                        //       bubbles: true,
                        //       composed: true,
                        //       detail: {
                        //         actionName:
                        //         REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                        //             .initiateAnimationCompletion,
                        //         data: {
                        //           showCompletionAnimation: true,
                        //           successAnimationLength: 1500,
                        //           skipCurtainCloseAnimation: false,
                        //         },
                        //       },
                        //     })
                        // );
                      }
                    }}"
                    ,
                    @stop-animation="${() => {
                      if (!this.hasSeenStopAnimation) {
                        this.hasSeenStopAnimation = true;
                        this.dispatchEvent(
                          new CustomEvent('host-app-event', {
                            bubbles: true,
                            composed: true,
                            detail: {
                              actionName:
                                REFI_VEHICLE_LOAN_CONST.hostAppEventActionName
                                  .initiateAnimationCompletion,
                              data: {
                                showCompletionAnimation: true,
                                successAnimationLength: 1500,
                                skipCurtainCloseAnimation: false,
                              },
                            },
                          })
                        );
                      }
                    }}"
                  ></refi-iconic-vehicle-and-loan-container>
                </div>
              `}
        `;
      }
      return html`
        ${this.preload === 'true'
          ? ''
          : html`
              <div class="main" role="main">
                <refi-vehicle-and-loan-container></refi-vehicle-and-loan-container>
              </div>
            `}
      `;
    }

    return html`<refi-loading-spinner
      message="${REFI_VEHICLE_LOAN_CONST.LOADING_SEARCH_TEXT}"
    ></refi-loading-spinner>`;
  }

  disconnectedCallback() {
    this.preload = 'false';
    super.disconnectedCallback();
  }
}

customElements.define('refi-vehicle-and-loan-app', RefiVehicleAndLoanApp);
